import { ChangeDetectionStrategy, Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { SimpleChartWithTableComponent } from 'app/trello/components/shared/visualisations/simple-chart-with-table/simple-chart-with-table.component';
import { GroupedMap, GroupedMap2D } from 'app/trello/models/board-summary';
import { ChartableData, LabelledValues } from 'app/trello/models/charts';
import { ReadableSecondsPipe } from 'app/trello/pipes/readable-seconds.pipe';
import { ChartOptions, ChartType, LegendOptions, TooltipOptions } from 'chart.js';
import cloneDeep from 'lodash-es/cloneDeep';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SimpleChartWithTableComponent,
  ],
  providers: [ReadableSecondsPipe],
  selector: 'app-trend-chart-with-table',
  template: `
      <app-simple-chart-with-table
              [chartData]="mappedChartData"
              [chartType]="chartType"
              [chartLegend]="chartLegend"
              [chartOptions]="chartOptions"
              [tableTitle]="tableTitle"
              [tableGroupColumnLabel]="tableGroupColumnLabel"></app-simple-chart-with-table>
  `,
})
export class TrendChartWithTableComponent<T, U> implements OnInit, OnChanges {

  @Input({required: true}) chartData!:
    ChartableData<LabelledValues, GroupedMap<T[]> | GroupedMap2D<U[]>> |
    ChartableData<LabelledValues, GroupedMap<T[]> | GroupedMap2D<U[]>>[];

  @Input() chartLegend: boolean = true;

  @Input() stacked: boolean = false;
  @Input() fill: boolean = false;

  @Input() tableTitle: string | undefined;
  @Input() tableGroupColumnLabel: string = 'Group';

  @Input() valueFormat: 'number' | 'human-readable-time' | 'custom' = 'number';
  @Input() valueUnit: 'number' | 'milliseconds' | 'seconds' | 'minutes' | 'hours' | 'days' = 'number';

  @Input() valueFormatter: (value: any) => string = (value: any) => String(value);

  protected readonly chartType: ChartType = 'line';
  protected chartOptions!: ChartOptions;

  protected mappedChartData!:
    ChartableData<LabelledValues, GroupedMap<T[]> | GroupedMap2D<U[]>> |
    ChartableData<LabelledValues, GroupedMap<T[]> | GroupedMap2D<U[]>>[];

  constructor(
    private readonly readableSecondsPipe: ReadableSecondsPipe,
  ) {
  }

  public ngOnInit(): void {

    this.prepareChartData();
    this.setChartOptions();
  }

  public ngOnChanges(
    changes: SimpleChanges,
  ): void {

    if (changes['chartData']
      || changes['fill']) {
      this.prepareChartData();
    }

    if (changes['stacked']
      || changes['valueFormat']
      || changes['valueUnit']) {
      this.setChartOptions();
    }
  }

  private prepareChartData(): void {

    if (!this.chartData) {
      this.mappedChartData = [];
      return;
    }

    if (Array.isArray(this.chartData)) {

      this.mappedChartData =
        this.chartData.map((chartDataPoint) => {

          return {
            ...(cloneDeep(chartDataPoint)),
            fill: chartDataPoint.fill !== undefined ? chartDataPoint.fill : this.fill,
          } as ChartableData<LabelledValues, GroupedMap<T[]> | GroupedMap2D<U[]>>;
        });

    } else {

      this.mappedChartData = {
        ...(cloneDeep(this.chartData)),
        fill: this.chartData.fill !== undefined ? this.chartData.fill : this.fill,
      } as ChartableData<LabelledValues, GroupedMap<T[]> | GroupedMap2D<U[]>>;
    }
  }

  private setChartOptions(): void {

    this.chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: 'x',
      interaction: {
        axis: 'x',
        mode: 'index',
        intersect: false,
      },
      scales: {
        x: {
          display: true,
          stacked: this.stacked,
          type: 'category',
          title: {
            display: false,
            text: '',
          },
          ticks: {
            autoSkip: true,
            maxTicksLimit: 20,
            maxRotation: 0,
            minRotation: 0,
          },
        },
        y: {
          display: true,
          beginAtZero: true,
          stacked: this.stacked,
          title: {
            display: false,
            text: '',
          },
          ticks: {
            precision: 0,
            callback: (value: string | number) => this.formatValue(Number(value)),
          },
        },
      },
      plugins: {
        legend: {
          display: true,
        } as LegendOptions<'line'>,
        tooltip: {
          position: 'nearest',
          callbacks: {
            label: (context) => {

              const label = context.dataset.label || '';
              const value = context.parsed.y;

              return `${label}: ${this.formatValue(value)}`;
            },
          },
        } as TooltipOptions<'line'>,
        noTooltipColorBoxBorders: {
          enabled: true,
        },
        timeBasedTicks: {
          valueAxis: 'y',
          valueUnit: this.valueUnit,
        },
      } as any,
    };
  }

  private formatValue(
    value: any,
  ): string {

    if (this.valueFormat === 'custom') {
      return this.valueFormatter(value);
    }

    if (this.valueFormat === 'human-readable-time') {

      const seconds = Number(value) / 1000;

      return this.readableSecondsPipe.transform(seconds, 1) || '';
    }

    return String(value);
  }

}
